import React, { useEffect, useRef, useState } from "react";
import { Button, Card, Row, Stack } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { deleteGuardian, deleteMember, postNote, patchCase, patchGuardian, patchMember } from "../../services/httpClient";
import { User } from "../../types/user";
import ExitNote from "./ExitNote";
import { Member } from "../../types/member";

interface DeleteState {
  setIsDeleting: any;
  userToDelete: User | Member | undefined;
  type: string | undefined;
  onDone: any;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
  guardians: any[];
  isPrimary: boolean;
  caseStatus: string | undefined;
  family: any[];
  fetchHousehold: any;
  getCase: any;
}

const ConfirmDelete = ({
  setIsDeleting,
  userToDelete,
  type,
  onDone,
  setShowToast,
  setToastText,
  setToastBody,
  guardians,
  isPrimary,
  caseStatus,
  family,
  fetchHousehold,
  getCase,
}: DeleteState) => {
  const { id } = useParams();
  const [exitDate, setExitDate] = useState<string>(new Date().toISOString().split("T")[0]);
  const [exitNote, setExitNote] = useState<string>();
  const [exitType, setExitType] = useState<string>("voluntary");
  const [newMainContact, setNewMainContact] = useState<string>("");
  const reassignMainContact = !!(isPrimary && (guardians?.filter((g: any) => g.id !== userToDelete?.id).length || 0) > 0);
  const name = userToDelete && `${userToDelete.firstname} ${userToDelete.lastname}`;

  const totalActiveUsers = family.filter((f: any) => !f.deleted_at).length;
  const isLastUser = totalActiveUsers === 1;

  const mainRef = useRef();

  const mainContactOptions = guardians
    .filter((g: any) => g.id !== userToDelete?.id && !g.is_banned)
    .map((g: any) => ({
      id: g.id,
      key: g.id,
      name: `${g.firstname} ${g.lastname}`,
    }));

  const handleScroll = (ref: any) => {
    if (ref.current) {
      ref.current.scrollIntoView({ behavior: "instant", block: "end", inline: "nearest" });
    }
  };

  useEffect(() => {
    handleScroll(mainRef);
  }, []);

  const onClickDeleteFinalWExit = () => {
    const deleteUserAndSaveNote = () => {
      if (type === "Guardian") {
        const note = {
          notetype: "exit",
          subject: {
            id: userToDelete?.id,
            type: "guardian",
          },
          fields: {
            exit_type: exitType,
            description: exitNote,
            date: new Date().toISOString().split("T")[0],
          },
        };

        //save exit note
        postNote(note, id)
          .then((res: any) => {
            const note_id = res[0].data.note_id;

            const exit = {
              exit_at: exitDate,
              exit_note_id: note_id,
            };

            deleteGuardian(id, userToDelete?.id, exit)
              .then((res: any) => {
                setShowToast(true);
                setToastText("Guardian Deleted");
                setToastBody(name);
                onDone();
                //delay
                setTimeout(() => {
                  setIsDeleting(false);
                }, 1000);
              })
              .catch((err: any) => {
                console.log(err);
              });
          })
          .catch((err: any) => {
            console.log(err);
          });
      } else if (type === "Member") {
        const note = {
          notetype: "exit",
          subject: {
            id: userToDelete?.id,
            type: "member",
          },
          fields: {
            exit_type: exitType,
            description: exitNote,
            date: new Date().toISOString().split("T")[0],
          },
        };

        //save exit note
        postNote(note, id)
          .then((res: any) => {
            const note_id = res[0].data.note_id;

            const exit = {
              exit_at: exitDate,
              exit_note_id: note_id,
            };

            deleteMember(id, userToDelete?.id, exit)
              .then((res: any) => {
                setShowToast(true);
                setToastText("Guardian Deleted");
                setToastBody(name);
                onDone();
                //delay
                setTimeout(() => {
                  setIsDeleting(false);
                }, 1000);
              })
              .catch((err: any) => {
                console.log(err);
              });
          })
          .catch((err: any) => {
            console.log(err);
          });
      }
    };

    // reassign main contact
    if (type === "Guardian" && isPrimary && newMainContact) {
      const updated = { is_primary: true };
      patchGuardian(updated, id, newMainContact)
        .then(() => deleteUserAndSaveNote())
        .catch((err: any) => {
          console.log(err);
          deleteUserAndSaveNote();
        });
    } else {
      deleteUserAndSaveNote();
    }
  };

  const onClickDeleteFinal = () => {
    const deleteUser = () => {
      if (type === "Guardian") {
        deleteGuardian(id, userToDelete?.id)
          .then((res: any) => {
            setShowToast(true);
            setToastText("Guardian Deleted");
            setToastBody(name);
            onDone();
            //delay
            setTimeout(() => {
              setIsDeleting(false);
            }, 1000);
          })
          .catch((err: any) => {
            console.log(err);
          });
      } else if (type === "Member") {
        deleteMember(id, userToDelete?.id)
          .then((res: any) => {
            setShowToast(true);
            setToastText("Guardian Deleted");
            setToastBody(name);
            onDone();
            //delay
            setTimeout(() => {
              setIsDeleting(false);
            }, 1000);
          })
          .catch((err: any) => {
            console.log(err);
          });
      }
    };

    // reassign main contact
    if (type === "Guardian" && isPrimary && newMainContact) {
      const updated = { is_primary: true };
      patchGuardian(updated, id, newMainContact)
        .then(() => deleteUser())
        .catch((err: any) => {
          console.log(err);
          deleteUser();
        });
    } else {
      deleteUser();
    }
  };

  const onClickDeactivateCase = async () => {
    // Deactivate the case
    await patchCase({ casestatus: "inactive" }, id);

    // create exit note for the current user
    if (caseStatus === "active") {
      const note = {
        notetype: "exit",
        subject: {
          id: userToDelete?.id,
          type: type === "Guardian" ? "guardian" : "member",
        },
        fields: {
          exit_type: exitType,
          description: exitNote ?? "",
          date: new Date().toISOString().split("T")[0],
        },
      };
      const res = await postNote(note, id);
      const noteId = res[0].data.note_id;

      const exitData = {
        exit_at: exitDate,
        exit_note_id: noteId,
      };

      if (type === "Guardian") {
        await patchGuardian(exitData, id, userToDelete?.id);
      } else {
        await patchMember(exitData, id, userToDelete?.id);
      }
    }

    setShowToast(true);
    setToastText("Case Deactivated");
    setToastBody(`Case has been made inactive`);
    fetchHousehold();
    getCase();
    setTimeout(() => {
      setIsDeleting(false);
    }, 1000);
  };

  const onClickCancelDelete = () => {
    setIsDeleting(false);
  };

  return (
    <>
      <Card className="bg-light mb-3">
        <Card.Body>
          {isLastUser && caseStatus === "active" ? (
            <>
              <Stack direction="horizontal" gap={3} className="mb-3">
                <Stack className="justify-content-center">
                  <Card.Subtitle>
                    You cannot delete the last person in a case. Would you like to change the case status to Inactive?
                  </Card.Subtitle>
                </Stack>
                <Button className="app-danger-color button text-nowrap" onClick={onClickDeactivateCase}>
                  Deactivate Case
                </Button>
                <div className="vr" />
                <Button className="app-primary-color button" onClick={onClickCancelDelete}>
                  Cancel
                </Button>
              </Stack>
              <ExitNote
                exitDate={exitDate}
                setExitDate={setExitDate}
                exitNote={exitNote}
                setExitNote={setExitNote}
                exitType={exitType}
                setExitType={setExitType}
                isBanned={false}
                setIsBanned={() => {}}
                reassignMainContact={reassignMainContact}
                newMainContact={newMainContact}
                setNewMainContact={setNewMainContact}
                mainContactOptions={mainContactOptions}
              />
            </>
          ) : isLastUser && caseStatus !== "active" ? (
            <>
              <Stack direction="horizontal" gap={3} className="mb-3">
                <Stack style={{ justifyContent: "center" }}>
                  <Card.Subtitle>You cannot delete the last member of a case.</Card.Subtitle>
                </Stack>
                <Button className="app-primary-color button" onClick={() => onClickCancelDelete()}>
                  Back
                </Button>
              </Stack>
            </>
          ) : (
            <>
              <Stack direction="horizontal" gap={3}>
                <Stack style={{ justifyContent: "center" }}>
                  <Card.Subtitle>
                    Are you sure you want to delete {userToDelete?.firstname} {userToDelete?.lastname}?
                  </Card.Subtitle>
                </Stack>
                <Button
                  className="app-danger-color button"
                  onClick={() => {
                    userToDelete?.exit_at ? onClickDeleteFinal() : onClickDeleteFinalWExit();
                  }}
                >
                  Confirm Delete
                </Button>
                <div className="vr" />
                <Button className="app-primary-color button" onClick={() => onClickCancelDelete()}>
                  Cancel
                </Button>
              </Stack>
              {!userToDelete?.exit_at && (
                <>
                  <div className="mt-3"></div>
                  <ExitNote
                    exitDate={exitDate}
                    setExitDate={setExitDate}
                    exitNote={exitNote}
                    setExitNote={setExitNote}
                    exitType={exitType}
                    setExitType={setExitType}
                    isBanned={false}
                    setIsBanned={() => {}}
                    reassignMainContact={reassignMainContact}
                    newMainContact={newMainContact}
                    setNewMainContact={setNewMainContact}
                    mainContactOptions={mainContactOptions}
                  />
                </>
              )}
            </>
          )}
        </Card.Body>
      </Card>
      <Row ref={mainRef} className="mb-5"></Row>
    </>
  );
};

export default ConfirmDelete;
